import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

function fmtDuration(seconds) {
  if (!seconds || seconds < 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function fmtPct(fraction) {
  if (fraction == null) return '—';
  return `${Math.round(fraction * 100)}%`;
}

function fmtAbsoluteTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

// Horizontal timeline showing focused / blurred / hidden bands. Built by
// walking the events in the same order the summarizer does, so what you see
// here lines up with the numbers in the summary card.
function FocusTimeline({ events, firstAt, lastAt }) {
  const bands = useMemo(() => {
    if (!firstAt || !lastAt) return [];
    const start = new Date(firstAt).getTime();
    const end = new Date(lastAt).getTime();
    const span = end - start;
    if (span <= 0) return [];

    const state = { tab_visible: true, window_focused: true };
    const segments = [];
    let prevAt = start;

    const bucket = () => {
      if (!state.tab_visible) return 'hidden';
      if (!state.window_focused) return 'blurred';
      return 'focused';
    };

    events.forEach((evt) => {
      const at = new Date(evt.occurred_at).getTime();
      if (at <= prevAt) return;
      segments.push({
        start: ((prevAt - start) / span) * 100,
        width: ((at - prevAt) / span) * 100,
        kind: bucket(),
      });
      switch (evt.event_type) {
        case 'tab_hidden':     state.tab_visible = false; break;
        case 'tab_visible':    state.tab_visible = true; break;
        case 'window_blurred': state.window_focused = false; break;
        case 'window_focused': state.window_focused = true; break;
        default: break;
      }
      prevAt = at;
    });
    if (prevAt < end) {
      segments.push({
        start: ((prevAt - start) / span) * 100,
        width: ((end - prevAt) / span) * 100,
        kind: bucket(),
      });
    }
    return segments;
  }, [events, firstAt, lastAt]);

  if (bands.length === 0) {
    return (
      <p className="text-xs text-gray-500">Not enough data to draw a timeline.</p>
    );
  }

  return (
    <div>
      <div className="relative h-6 rounded-md overflow-hidden bg-gray-100">
        {bands.map((b, i) => (
          <div
            key={i}
            title={b.kind}
            className={
              b.kind === 'focused' ? 'absolute h-full bg-emerald-500'
              : b.kind === 'blurred' ? 'absolute h-full bg-amber-400'
              : 'absolute h-full bg-rose-400'
            }
            style={{ left: `${b.start}%`, width: `${b.width}%` }}
          />
        ))}
      </div>
      <div className="mt-2 flex gap-4 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Focused</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400" /> Window blurred</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-rose-400" /> Tab hidden</span>
      </div>
    </div>
  );
}

const PAGE_SIZE = 50;

function EventTable({ events }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(events.length / PAGE_SIZE) || 1;
  const slice = events.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div>
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">When</th>
              <th className="px-3 py-2 text-left font-semibold">Event</th>
              <th className="px-3 py-2 text-left font-semibold">Payload</th>
            </tr>
          </thead>
          <tbody>
            {slice.map((e) => (
              <tr key={e.id} className="border-t border-gray-100">
                <td className="px-3 py-2 text-xs text-gray-500 font-mono whitespace-nowrap">
                  {fmtAbsoluteTime(e.occurred_at)}
                </td>
                <td className="px-3 py-2 text-sm text-gray-800">{e.event_type}</td>
                <td className="px-3 py-2 text-xs text-gray-500 font-mono">
                  {Object.keys(e.payload || {}).length > 0 ? JSON.stringify(e.payload) : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
          <span>Page {page + 1} of {pageCount} ({events.length} events)</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded border border-gray-200 bg-white px-2 py-1 disabled:opacity-40 cursor-pointer"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={page >= pageCount - 1}
              className="rounded border border-gray-200 bg-white px-2 py-1 disabled:opacity-40 cursor-pointer"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtPctFaceCheck(p) {
  return p == null ? '—' : `${(p * 100).toFixed(1)}%`;
}

function FaceStatusBadge({ recording }) {
  const s = recording.face_analysis_status;
  const map = {
    pending:  'bg-gray-100 text-gray-700 border-gray-200',
    running:  'bg-amber-50 text-amber-800 border-amber-200',
    complete: recording.face_check_passed
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-rose-50 text-rose-700 border-rose-200',
    failed:   'bg-rose-50 text-rose-700 border-rose-200',
    skipped:  'bg-gray-100 text-gray-500 border-gray-200',
  };
  const label = s === 'complete'
    ? (recording.face_check_passed ? `pass ${fmtPctFaceCheck(recording.face_present_pct)}` : `fail ${fmtPctFaceCheck(recording.face_present_pct)}`)
    : (s || 'unknown');
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${map[s] || 'bg-gray-50'}`}>
      {label}
    </span>
  );
}

function ConfigBanner({ enabled, urlSet, secretSet }) {
  const missing = [];
  if (!enabled) missing.push('FACE_ANALYSIS_ENABLED=true');
  if (!urlSet) missing.push('FACE_ANALYZER_URL');
  if (!secretSet) missing.push('FACE_ANALYZER_SHARED_SECRET');
  if (missing.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
      <span className="font-semibold">Auto-dispatch disabled.</span> Missing/unset: <code className="font-mono">{missing.join(', ')}</code>.
      Manual <strong>Trigger</strong> still works (it bypasses the flag).
    </div>
  );
}

function DebugLogPanel({ log }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [log]);
  if (!log) return null;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
        <div className="text-sm font-semibold text-gray-800">Face analyzer debug log</div>
        <div className="text-xs text-gray-500">captured from Rails.logger during perform_now</div>
      </div>
      <pre
        ref={ref}
        className="m-0 p-4 max-h-80 overflow-auto text-xs font-mono text-gray-700 whitespace-pre-wrap break-words bg-gray-50"
      >
        {log}
      </pre>
    </div>
  );
}

function RecordingPanel({ recording }) {
  const [expanded, setExpanded] = useState(false);
  const [playbackError, setPlaybackError] = useState(null);
  const [triggering, setTriggering] = useState(false);

  if (!recording) return null;

  const onTrigger = () => {
    if (!recording.analyze_url) return;
    if (recording.face_analysis_status === 'running' && !confirm('Recording is currently marked as running. Re-trigger anyway? (Resets state and runs perform_now synchronously)')) return;
    setTriggering(true);
    router.post(recording.analyze_url, {}, {
      preserveScroll: true,
      onFinish: () => setTriggering(false),
    });
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-gray-800">Viewer recording</p>
          <FaceStatusBadge recording={recording} />
          {recording.face_frames_with_face != null && recording.face_frames_analyzed != null && (
            <span className="text-[11px] tabular-nums text-gray-500">
              {recording.face_frames_with_face} / {recording.face_frames_analyzed} frames
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {recording.stream_url && (
            <button
              type="button"
              onClick={() => { setPlaybackError(null); setExpanded((v) => !v); }}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              {expanded ? 'Hide' : 'Watch'}
            </button>
          )}
          {recording.analyze_url && (
            <button
              type="button"
              disabled={triggering}
              onClick={onTrigger}
              className="inline-flex items-center rounded-md border border-indigo-300 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {triggering ? 'Running…' : (recording.face_analysis_status === 'complete' ? 'Re-run analysis' : 'Trigger analysis')}
            </button>
          )}
        </div>
      </div>

      {!recording.stream_url && (
        <div className="rounded border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
          Recording status: <span className="font-mono">{recording.status}</span>. No playable file available
          {recording.status === 'recording' && ' yet — the viewer is still in the room.'}
          {recording.status === 'purged' && ' — file was deleted from storage.'}
          .
        </div>
      )}

      {recording.face_analysis_error && (
        <p className="text-xs text-rose-600">{recording.face_analysis_error}</p>
      )}

      {expanded && recording.stream_url && (
        <div className="max-w-2xl">
          <video
            key={recording.stream_url}
            src={recording.stream_url}
            controls
            preload="metadata"
            onError={(e) => {
              const code = e.target?.error?.code;
              setPlaybackError(
                code === 4
                  ? 'Playback failed. The signed URL may have expired (5 min TTL). Reload the page to refresh.'
                  : 'Playback failed. Try downloading the file to play locally.'
              );
            }}
            onPlay={() => setPlaybackError(null)}
            className="w-full rounded-lg shadow-sm bg-black"
          />
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <a href={recording.stream_url} download className="text-indigo-600 hover:text-indigo-700">
              Download
            </a>
            <span>SAS URL expires after 5 minutes — re-open Watch to refresh.</span>
          </div>
          {playbackError && <p className="mt-2 text-xs text-rose-600">{playbackError}</p>}
        </div>
      )}
    </div>
  );
}

export default function SuperadminWebinarAttendanceDetail({ webinar, viewer, summary, events = [], recording, face_analyzer, debug_log }) {
  const [showEvents, setShowEvents] = useState(false);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5">
        <div>
          <Link
            href={`/superadmin/webinars/${webinar.id}/attendance`}
            className="text-sm text-indigo-600 hover:text-indigo-700"
          >
            ← Back to attendance
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-gray-900">{viewer.user_name}</h1>
          <p className="text-sm text-gray-500">{viewer.user_email}</p>
          <p className="text-sm text-gray-500">{webinar.title}</p>
        </div>

        {face_analyzer && (
          <ConfigBanner
            enabled={face_analyzer.enabled}
            urlSet={face_analyzer.url_set}
            secretSet={face_analyzer.secret_set}
          />
        )}

        <RecordingPanel recording={recording} />

        <DebugLogPanel log={debug_log} />

        {summary ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                label="Total time in room"
                value={fmtDuration(summary.room_total_seconds)}
                sub={summary.first_event_at && `Joined ${fmtAbsoluteTime(summary.first_event_at)}`}
              />
              <StatCard
                label="Focused time"
                value={fmtDuration(summary.focused_seconds)}
                sub={fmtPct(summary.focused_pct)}
              />
              <StatCard
                label="Away time"
                value={fmtDuration((summary.hidden_seconds || 0) + (summary.blurred_seconds || 0))}
                sub={`${summary.tab_hide_count} tab switches`}
              />
              <StatCard
                label="Face present"
                value={
                  summary.face_analysis_status === 'complete' && summary.face_present_pct != null
                    ? fmtPct(summary.face_present_pct)
                    : (summary.face_analysis_status || '—')
                }
                sub="Threshold 85%"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                label="Polls answered"
                value={`${summary.polls_answered} / ${summary.polls_offered}`}
                sub={summary.poll_avg_response_seconds != null
                  ? `Avg response ${summary.poll_avg_response_seconds}s`
                  : null}
              />
              <StatCard label="Chat messages" value={summary.chat_messages_sent} />
              <StatCard label="Slides viewed" value={summary.slides_viewed} />
              <StatCard label="Player pauses" value={summary.player_pause_count || 0} />
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-sm font-semibold text-gray-800 mb-3">Focus timeline</p>
              <FocusTimeline
                events={events}
                firstAt={summary.first_event_at}
                lastAt={summary.last_event_at}
              />
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-500">
            No summary computed yet — either the webinar hasn't ended or the viewer never opened the room.
          </div>
        )}

        <div>
          <button
            type="button"
            onClick={() => setShowEvents((v) => !v)}
            className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer"
          >
            {showEvents ? 'Hide' : 'Show'} raw event log ({events.length})
          </button>
          {showEvents && <div className="mt-3"><EventTable events={events} /></div>}
        </div>
      </div>
    </Layout>
  );
}
